import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { MatPaginator } from '@angular/material/paginator';
import { HttpErrorResponse } from '@angular/common/http';
import { AppModelModalComponent } from '@nimmsta/ng-commons-lib/forms';
import { AuthService } from '@nimmsta/ng-commons-lib/auth';
import {
  EmptyTableComponent,
  NimmstaDataSource,
} from '@nimmsta/ng-commons-lib/data-table';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import {
  Software,
  SoftwareStatus,
  SoftwareType,
  SoftwareVisibility,
  UserRole,
} from '@nimmsta/ng-commons-lib/models';
import { SoftwareService } from '@nimmsta/ng-commons-lib/services/software';
import { fromEvent, debounceTime, distinctUntilChanged, tap } from 'rxjs';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { SoftwareFormComponent } from './software-form.component';
import { SoftwareDatasource } from '../services/software/software.datasource';
import { SoftwareTypeService } from '../services/software/software-type.service';
import { NgClass, AsyncPipe } from '@angular/common';
import { SoftwareItemComponent } from './software-item.component';

_('software.title');

@Component({
  selector: 'app-software-list',
  templateUrl: './software-list.component.html',
  imports: [
    NgClass,
    RouterLink,
    TranslatePipe,
    SoftwareItemComponent,
    MatPaginator,
    SoftwareFormComponent,
    AsyncPipe,
    TranslatePipe,
    HeaderBarComponent,
    ErrorComponent,
    EmptyTableComponent,
    AppModelModalComponent,
  ],
})
export class SoftwareListComponent implements OnInit, AfterViewInit {
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private softwareTypeService = inject(SoftwareTypeService);
  private softwareService = inject(SoftwareService);

  softwareSource!: NimmstaDataSource<Software>;

  @ViewChild('EditModal', { static: true })
  private editModalComponent!: AppModelModalComponent<Software>;

  @ViewChild(SoftwareFormComponent, { static: true })
  private softwareFormComponent!: SoftwareFormComponent;

  @ViewChild(MatPaginator, { static: true })
  private paginator!: MatPaginator;

  @ViewChild('input', { static: true })
  private input!: ElementRef;

  isAdmin = false;

  error?: HttpErrorResponse;

  searchValue = '';

  softwareTypeId?: string;

  softwareType?: SoftwareType;

  protected header = 'software.title';

  ngOnInit(): void {
    this.softwareTypeId =
      this.route.snapshot.paramMap.get('typeId') ?? undefined;
    if (this.softwareTypeId) {
      this.softwareTypeService.get(this.softwareTypeId).subscribe((type) => {
        this.softwareType = type;
        this.header = `${this.translate.instant('software.title')} - ${
          type.name
        }`;
      });
    }
    this.softwareSource = new SoftwareDatasource(
      this.softwareService,
      this.softwareTypeId,
    );
    this.authService.getCurrentUser()?.subscribe((user) => {
      this.isAdmin = user.role === UserRole.ADMIN;
    });
    this.loadData();
    this.paginator.page.subscribe(() => {
      this.loadData();
    });
  }

  ngAfterViewInit(): void {
    fromEvent(this.input.nativeElement, 'keyup')
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.searchValue = this.input.nativeElement.value ?? '';
          // Reset page index, otherwise we might open a page that doesn't exist after searching
          this.paginator.pageIndex = 0;
          this.loadData();
        }),
      )
      .subscribe();
  }

  loadData() {
    this.softwareSource
      .loadData({
        search: this.searchValue,
        filter: [],
        sortColumn: '',
        sortOrder: '',
        pageNumber: this.paginator.pageIndex,
        pageSize: this.paginator.pageSize,
      })
      .subscribe({
        next: (data) => {
          this.softwareSource.data!.content = data.content;
          this.paginator.pageSize = data.size;
          this.paginator.pageIndex = data.number;
          this.paginator.length = data.totalElements;
        },
        error: (error) => {
          this.error = error;
        },
      });
  }

  new(): void {
    this.translate.get('software.new').subscribe((value) => {
      this.openModal(value, true, {
        status: SoftwareStatus.MAINTAINED,
        visibility: SoftwareVisibility.INTERNAL,
      } as Software);
    });
  }

  edit(id: string): void {
    this.translate.get('software.edit').subscribe((value) => {
      this.openModal(value, true, this.getSelectedObject(id)!);
    });
  }

  openModal(title: string, editable: boolean, software: Software): void {
    this.softwareFormComponent.editable = editable;
    this.softwareFormComponent.setValue(software);
    this.editModalComponent.title = title;
    this.editModalComponent.editable = editable;
    this.editModalComponent.show();
  }

  onSave(): void {
    this.softwareFormComponent.onSave();
  }

  onModalClose(): void {
    this.editModalComponent.hide();
    this.loadData();
  }

  getSelectedObject(id: string): Software | undefined {
    return this.softwareSource.data?.content.filter((d) => id === d.id).pop();
  }

  onLoading(loading: boolean): void {
    this.editModalComponent.loading = loading;
  }
}
